import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from 'core/store/reduxStore';
import {  getIncidentsResponseMapper } from 'modules/flywheel/mappers/incidentsCardsMapper';
import { assignIncidentRequestService, fetchAIAnalysisData, getIncidentsByPage, getIncidentsRequestRecursion, getIncidentsUpdatesRequestRecursion, setIncidentStatusRequestService } from 'modules/flywheel/services/incidentService';
import { fetchBaseUrl } from 'core/utils/common';
import { IRowProps } from 'core/components/global/tables/datagrid/dataGrid.type';
import { IHeadCell } from 'core/components/global/tables/datagrid/dataGridHeader/datagridHeader';
import { Toast as showToast } from "core/components/global/toast/toast";
import { t } from 'i18next';
import { incidentHeaders, userHeaders } from './headers';

interface IncidentsState {
  pageEvents: IRowProps[],
  userEvents: IRowProps[],
  weeklyEvents: IRowProps[],
  areUserEventsLoading: boolean,
  incidentHeaders: IHeadCell[],
  userHeaders: IHeadCell[],
  newestStateEventTs: number,
  isFetching: boolean,
  assignedToOrg: number,
  previewWidth: number,
  criticalToday: number,
  lastUpdatedTs: number,
  selectedRow: string,
  selectedType: string,
  openSideModal: boolean,
  openSummaryModal: boolean,
  totalRowCount: number,
  isExportLoading: boolean,
  isPageLoading: boolean,
  // Store a map of incident ID to similarity analysis
  incidentAnalysisMap: { [key: string]: string }
}

const initialState: IncidentsState = {
  isExportLoading: false,
  totalRowCount: 0,
  pageEvents: [],
  userEvents: [],
  weeklyEvents: [],
  areUserEventsLoading: false,
  selectedRow: "",
  selectedType: "",
  previewWidth: 0,
  openSideModal: true, // Set to true by default to keep drawer open
  openSummaryModal: false,
  incidentHeaders: incidentHeaders,
  userHeaders: userHeaders,
  newestStateEventTs: new Date((new Date()).getTime() - (7 * 24 * 60 * 60 * 1000)).getTime(),
  isFetching: true,
  assignedToOrg: 0,
  criticalToday: 0,
  lastUpdatedTs: 0,
  isPageLoading: false,
  incidentAnalysisMap: {}
};

/**
 * Adds messages to an existing or new conversation
 */
export const setIncidentStatus = createAsyncThunk<{aFAccessToken: string, status: string, id: string}, {aFAccessToken: string, status: string, id: string}, { state: RootState, dispatch: AppDispatch }>(
  'setIncidentStatus',
  async ({aFAccessToken, status, id}: {aFAccessToken: string, status: string, id: string}, { getState, dispatch }: { getState: () => RootState, dispatch: AppDispatch }) => {
    // Push the data to the DBs
    const config = { 'Authorization': aFAccessToken || '', 'Content-Type': 'application/json' };

    try {
      const response = await setIncidentStatusRequestService(status, id, config);
      
      if (response.status >= 300) {
        console.error(`${response.status} HTTP Response; error assigning status ${status} to ${id}`);
        showToast(`${response.status} HTTP Response; error assigning status ${status} to ${id}`, "error");
      } else {
        // Add the data locally
        dispatch(putEvent({'value': status, 'id': id, 'type': 'status'}));
      }
    } catch (error) {
      showToast(`Error assigning ${id} to ${status}`, "error");
    }
    return {'status': status, 'id': id, aFAccessToken: ""};
  }
);

/**
 * Adds messages to an existing or new conversation
 */
export const assignUserToIncident = createAsyncThunk<{aFAccessToken: string, email: string, id: string}, {aFAccessToken: string, email: string, id: string}, { state: RootState, dispatch: AppDispatch }>(
  'assignUserToIncident',
  async ({aFAccessToken, email, id}: {aFAccessToken: string, email: string, id: string}, { getState, dispatch }: { getState: () => RootState, dispatch: AppDispatch }) => {
    // Push the data to the DBs
    const config = { 'Authorization': aFAccessToken || '', 'Content-Type': 'application/json' };
    
    try {
      const response = await assignIncidentRequestService(email, id, config);
      if (response.status === 200) {
        // Add the data locally
        dispatch(putEvent({'value': email, 'id': id, 'type': 'email'}));
      } else {
        console.error(`${response.status} HTTP Response; error assigning ${email} to ${id}`);
        showToast(`Error assigning ${email} to ${id}`, "error");
      }
    } catch (error) {
      showToast(`Error assigning ${email} to ${id}`, "error");
    }
    return {'email': email, 'id': id, aFAccessToken: ""};
  }
);

export const setWeeklyIncidents = createAsyncThunk<void, string, { state: RootState, dispatch: AppDispatch }>(
  'setWeeklyIncidents',
  async (aFAccessToken, { getState, dispatch }: { getState: () => RootState, dispatch: AppDispatch }) => {
    const config = { 'Authorization': aFAccessToken || '', 'Content-Type': 'application/json' };
    let response = { 'records': [], 'totalCount': 0, 'newestStateEventTs': 0, 'offset': 0 };
    let totalCount = 0;
    let offset = 0;
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000)).getTime();
    
    // Fetch if no data has been fetched before      
    let allRecords = [];
    do {
      const response_data = await getIncidentsRequestRecursion(config, 1000, offset, oneWeekAgo);
      if (response_data) {
        // If the request was successful proceed
        response = response_data.data;
        
        // Process if there are records left in the offset/pagingation otherwise break
        if (response && response.records && response.records.length > 0) {
          allRecords = [...allRecords, ...response.records];
          totalCount += response.records.length;
          offset = response.offset
        } else {
          // No more records to fetch
          break;
        }
      } else {
        // No more records to fetch
        break;
      }
    } while (totalCount < response.totalCount);
    
    // Update the records locally for this page
    dispatch(
      setWeeklyEvents(
        getIncidentsResponseMapper(t, allRecords)
      )
    )
  }
);

export const updateWeeklyIncidents = createAsyncThunk<void, string, { state: RootState, dispatch: AppDispatch }>(
  'updateWeeklyIncidents',
  async (aFAccessToken, { getState, dispatch }: { getState: () => RootState, dispatch: AppDispatch }) => {
    const state = getState().incidents;
    const config = { 'Authorization': aFAccessToken || '', 'Content-Type': 'application/json' };
    
    // Update the records with changes made to the db since the last fetch
    const response_data = await getIncidentsUpdatesRequestRecursion(config, Math.round(state.lastUpdatedTs/1000));
    if (response_data.status === 200 && response_data.data && response_data.data.length > 0) {
      dispatch(setLastUpdated(Date.now()));
      dispatch(updateWeeklyEvents(getIncidentsResponseMapper(t, response_data.data)))
    }    
  }
);

export const updatePage = createAsyncThunk<void, string, { state: RootState, dispatch: AppDispatch }>(
  'updatePage',
  async (aFAccessToken, { getState, dispatch }: { getState: () => RootState, dispatch: AppDispatch }) => {
    const state = getState().incidents;
    const config = { 'Authorization': aFAccessToken || '', 'Content-Type': 'application/json' };
    // Update the records with changes made to the db since the last fetch
    const response_data = await getIncidentsUpdatesRequestRecursion(config, Math.round(state.lastUpdatedTs/1000));
    if (response_data.status === 200 && response_data.data && response_data.data.length > 0) {
      dispatch(setLastUpdated(Date.now()));
      dispatch(updatePageEvents(getIncidentsResponseMapper(t, response_data.data)))
    }
  }
);

export const setIncidentsByPage = createAsyncThunk<void, {target: string, aFAccessToken: string, size: number, page: number, order: string, orderBy: string, filters: object, search: string}, { state: RootState, dispatch: AppDispatch }>(
  'setIncidentsByPage',
  async ({target, aFAccessToken, size, page, order, orderBy, filters, search}, { getState, dispatch }: { getState: () => RootState, dispatch: AppDispatch }) => {
    const config = { 'Authorization': aFAccessToken || '', 'Content-Type': 'application/json' };   

    const response = await getIncidentsByPage(config, size, page, order, orderBy, filters, search);
    
    try {
      // Process if there are records left in the offset/pagingation, otherwise break
      if (response && response.data.records) {
        const records = getIncidentsResponseMapper(t, response.data);
        
        switch (target) {
          case 'home':
            // Update the pageEvents in the UI
            dispatch(
              setUserEvents(
                records
              )
            )

            dispatch(
              setTotalRowCount(response.data.totalRowCount)
            )

            break;
          case 'cases':
            // Update the pageEvents in the UI
            dispatch(
              setPageEvents(
                records
              )
            )

            dispatch(
              setTotalRowCount(response.data.totalRowCount)
            )

            break;
          default:
            console.error(`Unexpected target page ${target}`);
        }
      }
    } catch (error) {
      showToast(`500 HTTP Response, unable to fetch page data`, "error");
    }
  }
);

/**
 * Thunk to store incident analysis in Redux state
 */
export const storeIncidentAnalysis = createAsyncThunk<
  void, 
  {incidentId: string, analysis: string}, 
  { state: RootState, dispatch: AppDispatch }
>(
  'storeIncidentAnalysis',
  async ({incidentId, analysis}, { dispatch }) => {
    // Store the analysis in Redux
    dispatch(setIncidentAnalysis({ incidentId, analysis }));
  }
);

export const fetchAllIncidentsByPage = createAsyncThunk<{exportEvents: { records: any[], totalCount: number}}, {aFAccessToken: string, size: number, page: number, order: string, orderBy: string, filters: object, search: string}, { state: RootState, dispatch: AppDispatch }>(
  'fetchAllIncidentsByPage',
  async ({aFAccessToken, size, page, order, orderBy, filters, search}, { getState, dispatch }: { getState: () => RootState, dispatch: AppDispatch }) => {
    const config = { 'Authorization': aFAccessToken || '', 'Content-Type': 'application/json' };

    let totalCount = 0;
    let records:any = [];
    do {
      const response_data = await getIncidentsByPage(config, 1000, page, order, orderBy, filters, search);
      if (response_data && response_data.data) {
        const mapperResponse = getIncidentsResponseMapper(t, response_data.data);

        records = records.length === 0 ? mapperResponse : [...records, ...mapperResponse]
        totalCount = response_data.data.totalRowCount;
      }
      page += 1;
    } while (records.length < totalCount);

    return { 
      exportEvents: { records, totalCount } 
    };
  }
);

/**
 * The Messages Redux Slice
 */
export const incidentSlice = createSlice({
  name: 'incidents',
  initialState,
  reducers: {
    setIncidentAnalysis: (state, action: PayloadAction<{incidentId: string, analysis: string}>) => {
      state.incidentAnalysisMap[action.payload.incidentId] = action.payload.analysis;
    },
    setWeeklyEvents: (state, action: PayloadAction<IRowProps[]>) => {
      const response = [...action.payload];
      state.weeklyEvents = response;
    },
    setPageEvents: (state, action: PayloadAction<IRowProps[]>) => {
      const response = [...action.payload];
      state.pageEvents = response;
    },
    setUserFilter: (state, action: PayloadAction<IRowProps[]>) => {
      const response = [...action.payload];
      state.pageEvents = response;
    },
    putEvent: (state, action: PayloadAction<{value: string, id: string, type: string}>) => {
      state.pageEvents = state.pageEvents.map((item) => {
        if(action.payload.type === "email"){
          if (item.id === action.payload.id) {
            item.status = action.payload.value.trim() === "" ? "Open" : "In Progress";
            item.assignedto = action.payload.value;
          } 
        } else if (action.payload.type === "status") {
          if (item.id === action.payload.id) {
            item.status = action.payload.value.trim()
          } 
        }
        return item;
      })

      state.userEvents = state.userEvents.map((item) => {
        if(action.payload.type === "email"){
          if (item.id === action.payload.id) {
            item.status = action.payload.value.trim() === "" ? "Open" : "In Progress";
            item.assignedto = action.payload.value;
          } 
        } else if (action.payload.type === "status") {
          if (item.id === action.payload.id) {
            item.status = action.payload.value.trim()
          } 
        }
        return item;
      })
    },
    setUserEvents: (state, action: PayloadAction<IRowProps[]>) => {
      state.userEvents = action.payload;
    },
    setAreUserEventsLoading: (state, action: PayloadAction<boolean>) => {
      state.areUserEventsLoading = action.payload;
    },
    setRecursionNewestTs: (state, action: PayloadAction<number>) => {
      state.newestStateEventTs = Number(action.payload);
    },
    setSideModal: (state, action: PayloadAction<boolean>) => {
      try {
        state.openSideModal = Boolean(action.payload);
        console.debug("setSideModal called with:", action.payload);
        
        // If opening the side modal, set to a reasonable width if it was collapsed
        if (action.payload && state.previewWidth < 5) {
          state.previewWidth = 30;
        } else {
          if (action.payload === false) {
            state.previewWidth = 1.5;
          }
        }
      } catch (error) {
        console.error("Error in setSideModal:", error);
      }
    },
    setPreviewWidth: (state, action: PayloadAction<number>) => {
      try {
        state.previewWidth = action.payload;
      } catch (error) {
        console.error("Error in setPreviewWidth:", error);
      }
    },
    setSummaryModal: (state, action: PayloadAction<boolean>) => {
      state.openSummaryModal = Boolean(action.payload);
    },
    setTotalRowCount: (state, action: PayloadAction<number>) => {
      state.totalRowCount = action.payload;
    },
    setSelectedRow: (state, action: PayloadAction<{id: string, type: string}>) => {
      state.selectedRow = action.payload.id;
      state.selectedType = action.payload.type;
    },
    setExportLoading: (state, action: PayloadAction<boolean>) => {
      state.isExportLoading = action.payload;
    },
    updateWeeklyEvents: (state, action: PayloadAction<IRowProps[]>) => {
      if (action.payload.length > 0) {
        // Create a new state object to ensure immutability
        const newState = { ...state };
      
        // Iterate through each event in the payload
        action.payload.forEach(event => {

          // Find the corresponding row in newState.weeklyEvents based on the id
          const index = newState.weeklyEvents.findIndex(row => row.id === event.id);
          
          // If the row is found, update its status field
          if (index !== -1) {
            newState.weeklyEvents[index] = {
              ...newState.weeklyEvents[index],
              status: event.status,
              assignedto: event.assignedto
            };
          }
        });
      
        // Set the state
        state.weeklyEvents = newState.weeklyEvents;
      }
    },
    updatePageEvents: (state, action: PayloadAction<IRowProps[]>) => {
      if (action.payload.length > 0) {
        // Create a new state object to ensure immutability
        const newState = { ...state };
      
        // Iterate through each event in the payload
        action.payload.forEach(event => {

          // Find the corresponding row in newState.pageEvents based on the id
          const index = newState.pageEvents.findIndex(row => row.id === event.id);
          
          // If the row is found, update its status field
          if (index !== -1) {
            newState.pageEvents[index] = {
              ...newState.pageEvents[index],
              status: event.status,
              assignedto: event.assignedto
            };
          }
        });
      
        // Set the state
        state.pageEvents = newState.pageEvents;
      }
    },
    setLastUpdated: (state, action: PayloadAction<number>) => {
      state.lastUpdatedTs = action.payload;
    },
    setStateFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(
      assignUserToIncident.fulfilled, (state, action) => {
        // Print the outcome, better have a banner notice
        state.pageEvents = state.pageEvents.map((event) => {
          if (event.email === action.payload.email) {
            return({...event, assignedto: action.payload.email})
          } 
          return(event);
        })
      }
    );
    builder.addCase(
      fetchAllIncidentsByPage.fulfilled, () => {
      },
    );
    builder.addCase(setIncidentsByPage.pending, (state) => {
      if (state.userEvents.length == 0) {
        state.areUserEventsLoading = true;
      }
    });
    builder.addCase(setIncidentsByPage.fulfilled, (state) => {
      state.areUserEventsLoading = false;
    });
    builder.addCase(setIncidentsByPage.rejected, (state) => {
      state.areUserEventsLoading = false;
    });
    builder.addCase(
      setIncidentStatus.fulfilled, () => {
      },
    );
    builder.addCase(updateWeeklyIncidents.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(updateWeeklyIncidents.fulfilled, (state) => {
      state.isFetching = false;
    });
    builder.addCase(updateWeeklyIncidents.rejected, (state) => {
      state.isFetching = false;
    });
    builder.addCase(setWeeklyIncidents.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(setWeeklyIncidents.fulfilled, (state) => {
      state.isFetching = false;
    });
    builder.addCase(setWeeklyIncidents.rejected, (state) => {
      state.isFetching = false;
    });
    builder.addCase(updatePage.pending, (state) => {
      state.isFetching = true;
      state.isPageLoading = true;
    });
    builder.addCase(updatePage.fulfilled, (state) => {
      state.isFetching = false;
      state.isPageLoading = false;
    });
    builder.addCase(updatePage.rejected, (state) => {
      state.isFetching = false;
      state.isPageLoading = false;
    });
  },
});

export const { 
  setPageEvents,
  setWeeklyEvents,
  updatePageEvents,
  updateWeeklyEvents,
  putEvent,
  setStateFetching,
  setRecursionNewestTs,
  setLastUpdated,
  setSelectedRow,
  setTotalRowCount,
  setSideModal,
  setPreviewWidth,
  setSummaryModal,
  setExportLoading,
  setUserEvents,
  setAreUserEventsLoading,
  setIncidentAnalysis
} = incidentSlice.actions;

export default incidentSlice.reducer;